import Swiper from 'swiper';
import { Navigation, Pagination, Parallax } from 'swiper/modules';

function init( container ) {
	if ( ! ( container instanceof HTMLElement ) ) {
		return;
	}

	const swiperContainer = container.querySelector( '.timeline__swiper' );
	const nextEl = container.querySelector( '.timeline__next' );
	const prevEl = container.querySelector( '.timeline__prev' );
	const paginationEl = container.querySelector( '.timeline__pagination' );
	const yearCalloutEl = container.querySelector( '.timeline__year-callout' );
	const yearTextEl = yearCalloutEl?.querySelector( '.year-text' );

	if ( ! swiperContainer ) {
		return;
	}

	// get all year elements to use in pagination bullets
	const yearElements = container.querySelectorAll( '.timeline__slide-year' );
	const yearData = Array.from( yearElements ).map( ( el ) => {
		const yearText = el.textContent.trim();
		const yearNum = parseInt( yearText, 10 );
		return { text: yearText, year: yearNum };
	} );

	if ( yearData.length === 0 ) {
		return;
	}

	// calculate min and max years for proportional spacing
	const years = yearData.map( ( item ) => item.year );
	const minYear = Math.min( ...years );
	const maxYear = Math.max( ...years );
	const totalSpan = maxYear - minYear;

	// set initial year text
	if ( yearTextEl && yearData.length > 0 ) {
		yearTextEl.textContent = yearData[ 0 ].text;
	}

	// create inner wrapper for bullets if it doesn't exist
	let paginationInner = paginationEl.querySelector( '.timeline__pagination-track' );
	if ( ! paginationInner ) {
		paginationInner = document.createElement( 'div' );
		paginationInner.className = 'timeline__pagination-track';
		paginationEl.appendChild( paginationInner );
	}

	const inst = new Swiper( swiperContainer, {
		modules: [
			Navigation,
			Pagination,
			Parallax,
		],
		slidesPerView: 1,
		spaceBetween: 0,
		speed: 1000,
		grabCursor: false,
		longSwipes: false,
		preventInteractionOnTransition: true,
		parallax: true,
		navigation: {
			nextEl,
			prevEl,
		},
		pagination: {
			el: paginationInner,
			clickable: true,
			renderBullet: function ( index, className ) {
				const yearItem = yearData[ index ];
				const yearText = yearItem.text || '';

				// calculate position as percentage of total timeline
				const position = totalSpan > 0 ? ( ( yearItem.year - minYear ) / totalSpan ) * 100 : 0;

				// return bullet with percentage position
				return `<span class="${ className }" data-year="${ yearText }" data-position="${ position }" style="left: ${ position }%">${ yearText }</span>`;
			},
		},
		on: {
			init: function () {
				updateTimeline( this );
			},
			slideChange: function () {
				// update year callout animation
				if ( yearCalloutEl && yearData[ this.activeIndex ] ) {
					animateYearChange( yearCalloutEl, yearData[ this.activeIndex ].text );
				}

				// update timeline progress
				updateTimeline( this );
			},
		},
	} );

	// animate year change
	function animateYearChange( calloutEl, newYear ) {
		const currentYearEl = calloutEl.querySelector( '.year-text' );

		// add exit animation class to current year
		currentYearEl.classList.add( 'year-exit' );

		// after exit animation completes, update text and animate in
		setTimeout( () => {
			// create new element for the year text
			const newYearEl = document.createElement( 'span' );
			newYearEl.className = 'year-text year-enter';
			newYearEl.textContent = newYear;

			// replace the old element
			calloutEl.innerHTML = '';
			calloutEl.appendChild( newYearEl );

			// trigger reflow to ensure animation plays
			void newYearEl.offsetHeight;

			// remove the enter class to play the animation
			requestAnimationFrame( () => {
				newYearEl.classList.remove( 'year-enter' );
			} );
		}, 400 ); // match to CSS transition duration
	}

	// update the active timeline segment
	function updateTimeline( swiper ) {
		const bullets = paginationInner.querySelectorAll( '.swiper-pagination-bullet' );
		const activeIndex = swiper.activeIndex;

		// update progress line width based on current position
		if ( activeIndex >= 0 && bullets[ activeIndex ] ) {
			const currentBullet = bullets[ activeIndex ];
			const currentPosition = parseFloat( currentBullet.dataset.position ) || 0;
			paginationInner.style.setProperty( '--timeline-progress', `${ currentPosition }%` );
		}
	}

	return inst;
}

document.querySelectorAll( '.timeline' ).forEach( init );
